import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MyOrder } from 'ts/shared/interfaces/MOResponse';
import { getMyOrdersCurrencies, saveReport } from 'services/myOrders';
import { getStatus } from 'constants/mo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/ui/Button/Button';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import useModalClass from 'hooks/useModalClass';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
import withErrorBoundary from '../../withErrorBoundary';
import { createPortal } from 'react-dom';
import { showError, showSuccess } from 'utils/utils';
import useDraggableModal from 'hooks/useDraggableModal';

interface MoSaveReportModalProps {
  myOrder: MyOrder;
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  onSubmit: (myOrder: MyOrder) => void;
}

const parseNumber = (value: string): number => {
  const normalized = (value || '').replace(',', '.').trim();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toInputNumber = (value: number | undefined): string => {
  if (value == null || Number.isNaN(value)) {
    return '0';
  }
  return String(value);
};

const MoSaveReportModal: FC<MoSaveReportModalProps> = ({
  myOrder,
  openModal,
  setOpenModal,
  ...props
}) => {

  const [t] = useTranslation();
  const classList = useModalClass(openModal);
  const [isLoading, setIsLoading] = useState(false);
  const [currencies, setCurrencies] = useState<any[]>([]);
  console.log(myOrder.order.segments[0].saveReport?.sr_original_amount || myOrder.order.segments[0].sysTotalGross2);
  const [originalAmount, setOriginalAmount] = useState((myOrder.order.segments[0].saveReport?.sr_original_amount || myOrder.order.segments[0].sysTotalGross2) + '');
  const [refundAmount, setRefundAmount] = useState(myOrder.order.segments[0].saveReport?.sr_refund_amount + '');
  const [refundNcAmount, setRefundNcAmount] = useState(myOrder.order.segments[0].saveReport?.sr_refund_nc_amount + '');
  const [remark, setRemark] = useState(myOrder.order.segments[0].saveReport?.sr_remark || '');
  const [currency, setCurrency] = useState(myOrder.order.segments[0].saveReport?.sr_currency || myOrder.orderRow.currency || '');
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const { modalRef, modalStyle, isDragging, dragHandleProps } = useDraggableModal(openModal);
  const totalAmount = parseNumber(originalAmount) - parseNumber(refundAmount);

  useEffect(() => {
    if (!openModal) {
      return;
    }

    const sr = myOrder.fullSegment.saveReport;
    setOriginalAmount(toInputNumber(sr?.sr_original_amount || myOrder.order.segments[0].sysTotalGross2));
    setRefundAmount(toInputNumber(sr?.sr_refund_amount));
    setRefundNcAmount(toInputNumber(sr?.sr_refund_nc_amount));
    setRemark(sr?.sr_remark || '');
    setCurrency(sr?.sr_currency || myOrder.orderRow.currency || '');

    (async () => {
      const nextCurrencies = await getMyOrdersCurrencies();
      setCurrencies(nextCurrencies);
      if (!sr?.sr_currency && !myOrder.orderRow.currency && nextCurrencies.length) {
        setCurrency(String(nextCurrencies[0].value));
      }
    })();
  }, [myOrder, openModal]);

  const onClickSave = async () => {
    if (!currency) {
      showError('Currency is required');
      return;
    }

    setIsLoading(true);
    const response = await saveReport({
      orderId: myOrder.orderRow.orderId,
      segmentId: myOrder.fullSegment.orderSegId,
      originalAmount: parseNumber(originalAmount),
      refundAmount: parseNumber(refundAmount),
      ncRefundAmount: parseNumber(refundNcAmount),
      currency: currency,
      remark: remark,
    });

    if (response && !response.hasErrors) {
      if (response?.myOrder) {
        showSuccess('Report saved successfully');
        props.onSubmit(response.myOrder);
        setOpenModal(false);
      } else {
        showError(t('something_went_wrong'));
      }
    } else {
      showError(response?.errorMessage || t('something_went_wrong'));
    }
    setIsLoading(false);
  };

  return (
    <Fragment>
      {openModal && createPortal(
        <div
          className="modal-overlay modal-overlay-top mo-message-modal-overlay modal-maximizable" style={{ direction: 'ltr' }}
          ref={modalOverlayRef}
          onClick={(e) => e.target === e.currentTarget && setOpenModal(false)}
        >
          <div ref={modalRef} style={{ ...modalStyle, maxWidth: '980px' }} className={classList + ' modal'}>
            <div
              className={'modal-title draggable-modal-handle' + (isDragging ? ' dragging' : '')}
              {...dragHandleProps}
            >
              <h5 className="title">
                Save Report{' '}
                {myOrder?.fullSegment && (
                  <Fragment>
                    {' '}-{' '}Order: {myOrder.orderRow.orderId}{' '}
                    Segment:
                    {myOrder.fullSegment.orderSegId}{' '}
                    <span className={`mo-status ${getStatus(myOrder.orderRow.status)}`}>
                      {myOrder.orderRow.status}
                    </span>
                  </Fragment>
                )}
              </h5>
              <div className="modal-title-actions" data-no-drag="true">
                <ScreenshotSupport />
                <MaximizeModal modalOverlayRef={modalOverlayRef} />
                <FontAwesomeIcon icon={faTimes} onClick={() => setOpenModal(false)} />
              </div>
            </div>

            <div className="modal-body modal-body-overflow">
              <div className="mb-10">
                <FormGroup
                  label="Original Amount"
                  movablePlaceholder="Original Amount"

                  value={originalAmount}
                  onChange={(value) => setOriginalAmount(String(value || ''))}
                />
              </div>
              <div className="mb-10">
                <FormGroup
                  label="Refund Amount"
                  movablePlaceholder="Refund Amount"

                  value={refundAmount}
                  onChange={(value) => setRefundAmount(String(value || ''))}
                />
              </div>
              <div className="mb-10 form-group-yellow-color">
                <FormGroup
                  label="Refund NC Amount"
                  movablePlaceholder="Refund NC Amount"
                  value={refundNcAmount}
                  onChange={(value) => setRefundNcAmount(String(value || ''))}
                />
              </div>

              <div className="row mb-10">
                <div className="col-6">
                  <FormGroup
                    disabled={true}
                    label="Total Amount"
                    movablePlaceholder="Total Amount"

                    value={String(totalAmount)}
                    onChange={() => null}
                  />
                </div>
                <div className="col-6">
                  <SelectCustom
                    options={currencies}
                    value={currency}
                    onChange={(value) => setCurrency(String(value || ''))}
                    movablePlaceholder="Currency"

                    appendToBody
                  />
                </div>
              </div>

              <div className="form-group mb-10">
                <label>Remark</label>
                <textarea
                  className="form-control"
                  value={remark}
                  onChange={(e) => setRemark(String(e.target.value || ''))}
                  style={{ height: '150px', resize: 'none' }}
                />
              </div>
            </div>

            <div className="modal-footer">
              <Button
                disabled={isLoading}
                title={t('save')}
                className="btn btn-primary ml-0 mr-a"
                onClick={onClickSave}
              />
              <Button
                title={t('close')}
                className="btn btn-default"
                onClick={() => setOpenModal(false)}
              />
            </div>
          </div>
        </div>
        , document.body)}
    </Fragment>
  );
};

export default withErrorBoundary(MoSaveReportModal, 'MoSaveReportModal');
